'use client';

import { useEffect, useState } from 'react';
import { LiveIcon } from '@/app/components/icons';
import { BreakingNew, Channel } from '@/lib/model';
import { useSocket } from '@/hooks/useSocket';
import { BreakingNewsContent } from '@/app/components/notification';
import { useTranslations } from 'next-intl';
import { filterBreakingNews } from '@/lib/helper/setBreakingNewsRecords';

type Props = {
  initialBreakingNews?: BreakingNew[];
  channel: Channel;
};

export function BreakingNewsClient({ initialBreakingNews, channel }: Props) {
  const t = useTranslations();
  const { breakingNewsUpdates } = useSocket();
  const [breakingNews, setBreakingNews] = useState<BreakingNew[]>([]);

  // First initial breaking news filtering
  useEffect(() => {
    if (initialBreakingNews) {
      setBreakingNews(filterBreakingNews(initialBreakingNews));
    }
  }, [initialBreakingNews]);

  // Check every minute for 10 min feature
  useEffect(() => {
    const timer = setInterval(() => {
      setBreakingNews(filterBreakingNews(breakingNews));
    }, 60000);

    return () => clearInterval(timer);
  }, [breakingNews]);

  // If socket trigger, update or add new item
  useEffect(() => {
    const channelBreakingNewsUpdates = breakingNewsUpdates.filter(
      b => b.channel === channel?.id,
    );

    if (channelBreakingNewsUpdates.length > 0) {
      const updatedItem =
        channelBreakingNewsUpdates[channelBreakingNewsUpdates.length - 1];
      const findIndex = breakingNews?.findIndex(x => x.id === updatedItem?.id);
      if (findIndex < 0) {
        setBreakingNews(olds => [...olds, updatedItem]);
      } else {
        const newArray = breakingNews?.map(x =>
          x.id === updatedItem.id ? updatedItem : x,
        );
        setBreakingNews(newArray);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakingNewsUpdates]);

  if (breakingNews?.length === 0) return null;
  const slicedBreakingNews = breakingNews?.slice(0, 4);
  return (
    <div className="z-[10] relative w-full flex flex-col lg:flex-row">
      <div className="xl:min-w-[214px] lg:min-w-[188px] flex flex-row justify-center gap-[9px] bg-grey-3">
        <div className="my-auto">
          <LiveIcon className="text-breaking-text" />
        </div>

        <div className="font-body text-xl md:text-2xl font-extrabold text-breaking-text uppercase my-auto">
          {t('breaking')}
        </div>
      </div>

      <BreakingNewsContent breakingNews={slicedBreakingNews} />
    </div>
  );
}
